'use client'

import React, { useEffect } from "react";
import { usePathname } from "next/navigation";
import { UserContextProvider } from "./useUserContext";
import { NotificationProvider } from "@/components/PushNotification/NotificationProvider";
import { ChatProvider } from "@/components/Chat/ChatProvider";
import { AdminSupportChatProvider } from "@/components/Chat/AdminSupportChatProvider";
// PUBLIC_ROUTES is the list of URL prefixes for public pages (candidate portal,
// careers page) that should NOT receive any admin context providers.
// Defined in app/lib/constants.ts alongside other ATS route constants so it
// has a single source of truth. See the full documentation there.
import { PUBLIC_ROUTES } from "@/app/lib/constants";

// =============================================================================
// AppProviders — Wraps pages with all context providers needed by the admin app
// =============================================================================
// WHAT THIS COMPONENT DOES:
//   This is the single entry point for all React context providers in the app.
//   It wraps children with four providers in a nested chain:
//     1. UserContextProvider      — fetches user data, role, permissions, subscription
//     2. NotificationProvider     — sets up Firebase push notifications
//     3. AdminSupportChatProvider — sets up admin support chat (Firebase)
//     4. ChatProvider             — sets up dispatch/HR chat (Firebase)
//
//   The root layout (app/layout.tsx) wraps every page with this component.
//
// WHAT CHANGED:
//   Before this fix, every page — including public pages like /portal/ and
//   /careers/ — was wrapped with all four providers. This caused
//   UserContextProvider to call GET /api/user on public pages whenever an
//   admin was logged in, exposing sensitive data unnecessarily.
//
//   Now, we check the current URL using usePathname(). If the URL starts with
//   a public route prefix (see PUBLIC_ROUTES in app/lib/constants.ts), we skip
//   all providers and render children directly. This is safe because public
//   pages do not use any provider context (verified by searching all portal
//   and careers files).
//
//   For admin pages (everything else), we wrap children with all providers
//   exactly as before — no behavior change.
// =============================================================================
export const AppProviders = ({ children }: { children: React.ReactNode }) => {
  // usePathname() returns the current URL path (e.g., "/portal/abc123" or
  // "/ats/pipeline"). This is a Next.js client-side hook — it works because
  // this component is marked 'use client' at the top of the file.
  const pathname = usePathname();

  // Check if the current page is a public route by comparing the URL against
  // each prefix in PUBLIC_ROUTES. If any prefix matches, this page should NOT
  // receive admin providers.
  //
  // Example:
  //   pathname = "/portal/abc123token" → starts with "/portal/" → isPublicRoute = true
  //   pathname = "/careers/acme-corp"  → starts with "/careers/" → isPublicRoute = true
  //   pathname = "/ats/pipeline"       → matches nothing → isPublicRoute = false
  const isPublicRoute = PUBLIC_ROUTES.some((route) => pathname.startsWith(route));

  // PUBLIC ROUTES: Render children directly without any providers.
  // This prevents UserContextProvider from calling GET /api/user and stops
  // all other providers (notifications, chat) from initializing on pages
  // that don't need them.
  //
  // We wrap children in a Fragment (<>...</>) so React has a single root
  // element to render, same as the provider chain below.
  if (isPublicRoute) {
    return <>{children}</>;
  }

  // ADMIN ROUTES: Wrap children with all providers, exactly as before.
  // This is the original behavior — no change for any authenticated page.
  // UserContextProvider will call GET /api/user as expected, and all other
  // providers (notifications, chat) will initialize normally.
  return (
      <UserContextProvider>
        <NotificationProvider>
          <AdminSupportChatProvider>
            <ChatProvider>
              {children}
            </ChatProvider>
          </AdminSupportChatProvider>
        </NotificationProvider>
      </UserContextProvider>
  );
};
